import { useUserClientContext } from '@/app/clientContext';
import { Drawer } from '@/components';
import { Insight } from '@/views/map/Insight/Insight';
import { useMinDistToLineFormatter } from '@/views/list/Level3Item';
import { capitalize } from '@/helpers/stringConversion';
import { useAppDispatch } from '@/app/hooks';
import { clearItem } from '@/features/analysisMapSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { ItemHeading } from '@/views/map/GroupedItems/ItemHeading';
import { GoogleEvents } from '@/features/googleEvents';
import { LatLongMenu } from '@/views/map/LatLongMenu/LatLongMenu';
import classes from './HazardTreeMapPopover.module.css';

interface HazardTreeParams {
  heightInFt: number;
  bufferInFt: number;
  distInFt: number;
  treeType: string;
  id: string;
}

export const HazardTreeMapPopover = (props: {
  params: Partial<HazardTreeParams>;
  coordinates?: number[];
}) => {
  const { params, coordinates } = props;
  const { context } = useUserClientContext();
  const { getClientTerm } = useClientTerminology();
  const dispatch = useAppDispatch();

  const minDistanceToLine = useMinDistToLineFormatter(
    params.distInFt,
    params.bufferInFt,
    context.analysis_config.metric
  );

  return (
    <div className={classes.wrapper}>
      <Drawer
        onClose={() => dispatch(clearItem())}
        header={
          <div className={classes.header}>
            <ItemHeading
              name="Hazard tree"
              displayName={getClientTerm('map.modal.hazardTreeTitle')}
            />
          </div>
        }
      >
        <div className={classes.body}>
          {params.treeType && (
            <Insight
              name="Tree type"
              data={capitalize(params.treeType)}
              data-testid="hazard-tree-type"
            />
          )}
          {params.distInFt !== undefined && params.distInFt >= 0 && (
            <Insight
              name="Horizontal distance from line"
              data={minDistanceToLine}
              data-testid="hazard-tree-distance"
            />
          )}
          {coordinates && (
            <Insight
              name="Coordinates"
              data={coordinates
                .map((num) => num.toFixed(5))
                .reverse()
                .toString()}
              withCopy
              externalMenu={<LatLongMenu coordinates={coordinates} size={16} />}
              data-testid="hazard-tree-coordinates"
            />
          )}
          <Insight
            name="Overstory ID"
            data={params.id}
            googleEvent={GoogleEvents.copy_hazard_tree_name}
            withCopy
            data-testid="hazard-tree-overstory-id"
          />
        </div>
      </Drawer>
    </div>
  );
};
